<template lang="pug">
    .echart-container
        v-chart(
            :option="chartOption"
            style="height: 250px; width: 100%;"
            autoresize
        )
</template>

<script lang="ts">
import Vue from 'vue';

import { use } from 'echarts/core';
import { BarChart, ScatterChart } from 'echarts/charts';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([CanvasRenderer, BarChart, ScatterChart, TooltipComponent, GridComponent]);

interface Goal {
  friendly_created_at_time: string;
  friendly_start_time: string;
  current_value: number;
  target_value: number;
  recurring_type: string;
}

import { resolveCssVar } from "@/helpers/css";

export default Vue.extend({
    data() {
        return {
        }
    },
    methods: {
        formatGoalType(goalType: string): string {
            return goalType
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        getColorForGoalType(goalType: string): string {
            switch (goalType) {
                case 'inbound_phone_calls':
                    return resolveCssVar('--volie-blue-50', '#64b5f6');
                case 'outbound_phone_calls':
                    return resolveCssVar('--volie-blue', '#1976d2');
                case 'new_appointments':
                    return resolveCssVar('--purple', '#9c27b0');
                case 'same_day_appointments':
                    return resolveCssVar('--dark-blue', '#0d47a1');
                default:
                    return resolveCssVar('--volie-blue', '#1976d2');
            }
        },
        buildXAxisLabels(): string[] {
            if (this.goals.length === 0) return [];

            const recurringType = this.goals[0].recurring_type;

            const parseDate = (dateString: string) => {
                const match = dateString.match(/\d{2}\/\d{2}\/\d{4}/);
                if (!match) return null;
                const [month, day, year] = match[0].split('/');
                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            };

            const getQuarter = (month: number): number => {
                return Math.floor((month + 3) / 3);
            };

            switch (recurringType) {
                case 'daily':
                case 'weekly':
                    return this.goals.map(goal => {
                        const date = parseDate(goal.friendly_start_time);
                        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                    });
                case 'monthly':
                    return this.goals.map(goal => {
                        const date = parseDate(goal.friendly_start_time);
                        return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                    });
                case 'quarterly':
                    return this.goals.map(goal => {
                        const date = parseDate(goal.friendly_start_time);
                        const quarter = getQuarter(date.getMonth());
                        return `Q${quarter} (${date.getFullYear()})`;
                    });
                case 'yearly':
                    return this.goals.map(goal => {
                        const date = parseDate(goal.friendly_start_time);
                        return `${date.getFullYear()}`;
                    });
                default:
                    return this.goals.map(goal => goal.friendly_start_time);
            }
        },
    },
    computed: {
        chartOption() {
            const color = this.getColorForGoalType(this.goalType);
            const targetColor = resolveCssVar('--volie-blue-50', '#64b5f6');
            const xLabels = this.buildXAxisLabels();
            const currentValues = this.goals.map(g => g.current_value ?? 0);
            const targetValues = this.goals.map(g => g.target_value ?? 0);

            const allValues = [...currentValues, ...targetValues];
            const maxValue = Math.max(...allValues, 0);
            const yAxisMax = Math.ceil((maxValue * 1.1) / 10) * 10;

            return {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: (params) => {
                        const bar = params.find(p => p.seriesName === 'Current Value');
                        const target = params.find(p => p.seriesName === 'Target');
                        if (!bar) return '';
                        let result = `${bar.name}<br/>${bar.marker} Current: ${bar.value}`;
                        if (target) {
                            result += `<br/>${target.marker} Target: ${target.value}`;
                        }
                        return result;
                    },
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: xLabels,
                    axisLabel: {
                        rotate: 45,
                        fontSize: 10,
                    },
                },
                yAxis: {
                    type: 'value',
                    name: this.formatGoalType(this.goalType),
                    nameTextStyle: {
                        fontSize: 12,
                        fontWeight: 600,
                    },
                    min: 0,
                    max: yAxisMax || undefined,
                    axisLabel: {
                        formatter: (val) => Math.round(val),
                    },
                },
                series: [
                    {
                        name: 'Current Value',
                        type: 'bar',
                        barWidth: '55%',
                        data: currentValues,
                        itemStyle: {
                            color: color,
                            borderRadius: [3, 3, 0, 0],
                        },
                    },
                    {
                        name: 'Target',
                        type: 'scatter',
                        symbol: 'rect',
                        symbolSize: [30, 3],
                        data: targetValues,
                        itemStyle: {
                            color: targetColor,
                        },
                        z: 10,
                    },
                ],
            };
        },
    },
    components: {
        VChart,
    },
    props: {
        goals: {
            type: Array as () => Goal[],
            required: true
        },
        goalType: {
            type: String,
            required: true
        }
    },
    created() {
    },
    watch: {
    }
});
</script>

<style lang="scss" scoped>
</style>
